import {
  QStack,
  useCurrentUser,
  QFormControl,
  QText,
  QButton,
  useToastProvider,
  QBackLink,
  QSelect,
  QSpinner,
} from '@qualio/ui-components';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { Form, Formik } from 'formik';
import { createErrorToast } from '../../../utils/toast.utils';
import { useMcpEnabled } from '../../../hooks/useMcpEnabled';
import { getDevApiToken, updateDevApiToken } from '../../../api/medtech-api';
import { Header } from '../../../containers/header';
import { NavList, UsergroupsList } from '../../../utils/umfe.enums';
import { CacheKeys, Group } from '../../../types';
import { PermissionsFormElements } from '../../../components/permissions/PermissionsFormElements';
import { QualityEventsPermissionsFormFields } from '../../../components/FormComponents/QualityEventsPermissions';
import { SuppliersPermissionsFormFields } from '../../../components/FormComponents/SuppliersPermissions';
import { APICapabilitiesPermissionsFormFields } from '../../../components/FormComponents/APICapabilitiesPermissions';
import { DesignControlsPermissionsFormFields } from '../../../components/FormComponents/DesignControlsPermissions';
import { CompliancePermissionsFormFields } from '../../../components/FormComponents/CompliancePermissions';
import CheckBox from '../../../components/FormComponents/CheckBox';
import {
  CanAccessAPICapabilities,
  CanAccessCompliance,
  CanAccessDesignControls,
  CanAccessSuppliers,
  CanCreateIssue,
  CanManageQualityEventWorkflow,
  CanViewQE,
  CanWorkOnIssue,
} from '../../../utils/validation/medtechPermissions';
import { GroupsSelector } from './GroupsSelector';

type TokenEditFormValues = {
  role: (typeof UsergroupsList)[keyof typeof UsergroupsList];
  isAdmin: boolean;
} & QualityEventsPermissionsFormFields &
  SuppliersPermissionsFormFields &
  APICapabilitiesPermissionsFormFields &
  DesignControlsPermissionsFormFields &
  CompliancePermissionsFormFields;

type Props = {
  tokenId: number;
};

export const EditDevApiToken: React.FC<Props> = ({ tokenId }) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { showToast } = useToastProvider();
  const queryClient = useQueryClient();
  const [role, setRole] = useState(UsergroupsList.QUALITY);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const restrictUserMcpTokenActions = useMcpEnabled();

  const { isLoading, data: token } = useQuery(
    [CacheKeys.API_KEYS, currentUser.companyId, tokenId],
    () => getDevApiToken(currentUser.companyId, tokenId),
    {
      onSuccess: (data) => {
        if (restrictUserMcpTokenActions && data.is_real_user) {
          navigate(NavList.DEV_API.href);
          return;
        }
        const matchingRole = Object.values(UsergroupsList).find((r) => r.value === data.role);
        if (matchingRole) setRole(matchingRole);
        setGroups(data.groups);
      },
      onError: () => {
        showToast(createErrorToast(t('messages.error.showApiKeys'), 'dev-api-fetch-key-error-toast'));
        navigate(NavList.DEV_API.href);
      },
    },
  );

  const roleSelectOptions = useMemo(
    () =>
      Object.values(UsergroupsList).map(({ value, labelKey }) => ({
        value,
        label: t(labelKey),
      })),
    [t],
  );

  const resetPermissions = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
    setFieldValue('canViewQE', false);
    setFieldValue('canCreateQE', false);
    setFieldValue('canEditQE', false);
    setFieldValue('canManageQE', false);
    setFieldValue('canAccessSuppliers', false);
    setFieldValue('canAccessAPICapabilities', false);
    setFieldValue('canAccessDesignControls', false);
    setFieldValue('canAccessCompliance', false);
    setFieldValue('isAdmin', false);
    setRole(UsergroupsList.QUALITY);
  };

  const handleSubmit = async (values: TokenEditFormValues) => {
    const permissions: Record<string, boolean> = {
      [CanViewQE]: values.canViewQE,
      [CanCreateIssue]: values.canCreateQE,
      [CanWorkOnIssue]: values.canEditQE,
      [CanManageQualityEventWorkflow]: values.canManageQE,
      [CanAccessSuppliers]: values.canAccessSuppliers,
      [CanAccessAPICapabilities]: values.canAccessAPICapabilities,
      [CanAccessDesignControls]: values.canAccessDesignControls,
      [CanAccessCompliance]: values.canAccessCompliance,
    };

    setIsSubmitting(true);
    updateDevApiToken(currentUser.companyId, tokenId, {
      role: role.value,
      is_admin: values.isAdmin,
      permissions,
      group_ids: groups.map((g) => g.id),
    })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: [CacheKeys.API_KEYS, currentUser.companyId] });
        showToast({
          'data-cy': 'dev-api-update-token-success-toast',
          id: 'update-token',
          replace: true,
          title: t('messages.success.tokenUpdated'),
          status: 'success',
        });
        navigate(NavList.DEV_API.href);
      })
      .catch(() => {
        showToast(createErrorToast(t('messages.error.updateToken'), 'dev-api-update-token-error-toast'));
        setIsSubmitting(false);
      });
  };

  const navigateBackToList = () => navigate(NavList.DEV_API.href);

  if (isLoading || !token || (restrictUserMcpTokenActions && token.is_real_user)) {
    return <QSpinner size="sm" />;
  }

  const initialValues: TokenEditFormValues = {
    role: UsergroupsList.QUALITY,
    isAdmin: token.is_admin,
    canViewQE: token.permissions.includes(CanViewQE),
    canCreateQE: token.permissions.includes(CanCreateIssue),
    canEditQE: token.permissions.includes(CanWorkOnIssue),
    canManageQE: token.permissions.includes(CanManageQualityEventWorkflow),
    canAccessSuppliers: token.permissions.includes(CanAccessSuppliers),
    canAccessAPICapabilities: token.permissions.includes(CanAccessAPICapabilities),
    canAccessDesignControls: token.permissions.includes(CanAccessDesignControls),
    canAccessCompliance: token.permissions.includes(CanAccessCompliance),
  };

  return (
    <QStack maxWidth="720px">
      <QBackLink href={NavList.DEV_API.href} data-cy="back-api">
        {t('devApi.backToTokens')}
      </QBackLink>
      <Header content={t('devApi.editToken')} />
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {({ setFieldValue }) => (
          <Form>
            <fieldset disabled={isSubmitting} style={{ border: 'none', padding: 0, margin: 0 }}>
              <QStack spacing={4}>
                <QFormControl label={t('forms.labels.name')} data-cy="token-name-control">
                  <QText data-cy="token-name" fontWeight="medium">
                    {token.name}
                  </QText>
                </QFormControl>
                <QFormControl
                  label={t('forms.labels.role')}
                  helper={t('devApi.tokenRoleHelper')}
                  data-cy="role-control"
                >
                  <QSelect
                    data-cy="select-role"
                    data-testid="select-role"
                    options={roleSelectOptions}
                    value={role.value}
                    onChange={(v) => {
                      if (v) {
                        const matchingRole = Object.values(UsergroupsList).find(
                          (roleItem) => roleItem.value === v.value,
                        );
                        if (matchingRole) setRole(matchingRole);
                      }
                    }}
                  />
                </QFormControl>
                <CheckBox
                  name="isAdmin"
                  data-testid="isAdmin"
                  label={t('forms.labels.adminAccess')}
                  text={t('forms.labels.adminAccess')}
                  formLabel={t('forms.labels.adminAccess')}
                  helper={t('devApi.adminAccessHelper')}
                  data-cy="isAdmin"
                />
                <QFormControl
                  label={t('forms.labels.groups')}
                  helper={t('devApi.tokenGroupsHelper')}
                  data-cy="groups-control"
                >
                  <GroupsSelector onSelect={setGroups} selectedGroups={groups} />
                </QFormControl>
                <PermissionsFormElements resetPermissions={() => resetPermissions(setFieldValue)} isHumanUser={false} />

                <QStack direction="row">
                  <QButton data-cy="save-token-button" type="submit" isLoading={isSubmitting} isDisabled={isSubmitting}>
                    {t('common.save')}
                  </QButton>
                  <QButton onClick={navigateBackToList} variant="outline" isDisabled={isSubmitting}>
                    {t('common.cancel')}
                  </QButton>
                </QStack>
              </QStack>
            </fieldset>
          </Form>
        )}
      </Formik>
    </QStack>
  );
};
